/**
 * Generate a unique CSS selector for an element
 */
export function generateSelector(element: Element): string {
  // Try ID first (most specific)
  if (element.id) {
    const idSelector = `#${CSS.escape(element.id)}`;
    if (document.querySelectorAll(idSelector).length === 1) {
      return idSelector;
    }
  }

  // Try unique attributes
  const uniqueSelector = tryUniqueAttributes(element);
  if (uniqueSelector) {
    return uniqueSelector;
  }

  // Build path from root
  return buildSelectorPath(element);
}

/**
 * Try to find a unique selector using element attributes
 */
function tryUniqueAttributes(element: Element): string | null {
  const attributes = [
    'data-testid',
    'data-test',
    'data-cy',
    'aria-label',
    'name',
    'placeholder',
    'alt',
    'title'
  ];

  for (const attr of attributes) {
    const value = element.getAttribute(attr);
    if (value) {
      const selector = `[${attr}="${CSS.escape(value)}"]`;
      if (document.querySelectorAll(selector).length === 1) {
        return selector;
      }
    }
  }

  // Try class combinations
  if (element.classList.length > 0) {
    const classSelector = Array.from(element.classList)
      .map(cls => `.${CSS.escape(cls)}`)
      .join('');

    const tagWithClass = `${element.tagName.toLowerCase()}${classSelector}`;
    if (document.querySelectorAll(tagWithClass).length === 1) {
      return tagWithClass;
    }
  }

  return null;
}

/**
 * Build a selector path from root to element
 */
function buildSelectorPath(element: Element): string {
  const path: string[] = [];
  let current: Element | null = element;

  while (current && current !== document.body) {
    const selector = getElementSelector(current);
    path.unshift(selector);

    // Check if current path is unique
    const currentPath = path.join(' > ');
    if (document.querySelectorAll(currentPath).length === 1) {
      return currentPath;
    }

    current = current.parentElement;
  }

  return path.join(' > ');
}

/**
 * Get selector for a single element in its parent context
 */
function getElementSelector(element: Element): string {
  const tag = element.tagName.toLowerCase();

  // Use ID if available and unique
  if (element.id) {
    return `#${CSS.escape(element.id)}`;
  }

  // Use nth-child if no better option
  if (element.parentElement) {
    const siblings = Array.from(element.parentElement.children);
    const sameTagSiblings = siblings.filter(el => el.tagName === element.tagName);

    if (sameTagSiblings.length === 1) {
      return tag;
    }

    const index = sameTagSiblings.indexOf(element) + 1;
    return `${tag}:nth-of-type(${index})`;
  }

  return tag;
}

/**
 * Test if a selector is valid
 */
export function isValidSelector(selector: string): boolean {
  try {
    document.querySelector(selector);
    return true;
  } catch {
    return false;
  }
}

/**
 * Get all elements matching a selector
 */
export function queryAll(selector: string): Element[] {
  try {
    return Array.from(document.querySelectorAll(selector));
  } catch {
    return [];
  }
}

/**
 * Get single element matching a selector
 */
export function query(selector: string): Element | null {
  try {
    return document.querySelector(selector);
  } catch {
    return null;
  }
}

/**
 * Check if selector matches exactly one element
 */
export function isUniqueSelector(selector: string): boolean {
  return queryAll(selector).length === 1;
}

/**
 * Optimize selector by removing redundant parts
 */
export function optimizeSelector(selector: string): string {
  const parts = selector.split(' > ');

  // Try removing parts from the beginning
  for (let i = 0; i < parts.length - 1; i++) {
    const simplified = parts.slice(i).join(' > ');
    if (isUniqueSelector(simplified)) {
      return simplified;
    }
  }

  return selector;
}

/**
 * Generate a human-readable description of an element
 */
export function getElementDescription(element: Element): string {
  const tag = element.tagName.toLowerCase();

  if (element.id) {
    return `${tag}#${element.id}`;
  }

  if (element.classList.length > 0) {
    return `${tag}.${Array.from(element.classList).join('.')}`;
  }

  const text = element.textContent?.trim().substring(0, 30);
  if (text) {
    return `${tag} "${text}${text.length > 30 ? '...' : ''}"`;
  }

  return tag;
}
